import { DISCOVERY_LAB_ASSETS } from '../DiscoveryLabAssets.js';
import {
  calculateDiscoveryLabSafeBounds,
  isCompactDiscoveryLabLayout,
} from '../components/DiscoveryLabLayout.js';
import { RelationshipInvestigation } from '../minigames/relationship/RelationshipInvestigation.js';
import type {
  RelationshipInvestigationPhase,
  RelationshipInvestigationSnapshot,
} from '../minigames/relationship/RelationshipInvestigationState.js';
import { POLLINATOR_GARDEN_ASSETS } from './PollinatorGardenAssets.js';

const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';

interface PollinatorGardenInvestigationViewOptions {
  readonly reducedMotion: boolean;
  readonly onExit: () => void;
  readonly onComplete: () => void;
}

interface PanelButton {
  readonly container: Phaser.GameObjects.Container;
  readonly frame: Phaser.GameObjects.Image;
}

export class PollinatorGardenInvestigationView {
  private readonly root: Phaser.GameObjects.Container;
  private readonly background: Phaser.GameObjects.Image;
  private phaseLayer?: Phaser.GameObjects.Container;
  private readonly timers: Phaser.Time.TimerEvent[] = [];
  private suspended = false;
  private destroyed = false;
  private completionQueued = false;
  private hintPhase?: RelationshipInvestigationPhase;
  private hintStep = 0;
  private hintMessage = '';
  private feedbackMessage = '';

  constructor(
    private readonly scene: Phaser.Scene,
    readonly investigation: RelationshipInvestigation,
    private readonly options: PollinatorGardenInvestigationViewOptions,
  ) {
    this.root = scene.add.container(0, 0).setDepth(230);
    this.background = scene.add.image(
      640,
      360,
      POLLINATOR_GARDEN_ASSETS.gardenDormant.key,
    ).setDisplaySize(1580, 720);
    this.root.add(this.background);
    this.scene.input.keyboard?.on('keydown', this.handleKeyDown, this);
    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
    this.render();
  }

  layout() {
    if (!this.destroyed) this.render();
  }

  setSuspended(suspended: boolean) {
    this.suspended = suspended;
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    this.scene.input.keyboard?.off('keydown', this.handleKeyDown, this);
    this.scene.events.off(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
    this.timers.forEach(timer => timer.remove(false));
    this.timers.length = 0;
    this.scene.tweens.killTweensOf(this.root.getAll());
    this.root.destroy(true);
  }

  private render() {
    this.phaseLayer?.destroy(true);
    this.phaseLayer = this.scene.add.container(0, 0);
    this.root.add(this.phaseLayer);

    const snapshot = this.investigation.getSnapshot();
    if (this.hintPhase !== snapshot.phase) {
      this.hintPhase = snapshot.phase;
      this.hintStep = 0;
      this.hintMessage = '';
      this.feedbackMessage = '';
    }

    this.background.setTexture(
      snapshot.phase === 'complete'
        ? POLLINATOR_GARDEN_ASSETS.gardenRestored.key
        : POLLINATOR_GARDEN_ASSETS.gardenDormant.key,
    ).setDisplaySize(1580, 720);

    if (snapshot.phase === 'observe') this.renderObserve();
    else if (snapshot.phase === 'predict') this.renderPredict();
    else if (snapshot.phase === 'investigate') this.renderInvestigate(snapshot);
    else if (snapshot.phase === 'collect-evidence') this.renderEvidence(snapshot);
    else if (snapshot.phase === 'compare') this.renderCompare(snapshot);
    else if (snapshot.phase === 'apply') this.renderApply(snapshot);
    else this.renderComplete();
  }

  private renderObserve() {
    const safe = calculateDiscoveryLabSafeBounds(this.scene);
    const compact = isCompactDiscoveryLabLayout(this.scene, safe);
    this.createHeader('OBSERVE', 'Pollinator Garden • Two flower patches');

    const openPatch = this.createVisitedPatch(
      safe.centerX - (compact ? 220 : 255),
      safe.centerY + (compact ? 62 : 76),
      compact ? 205 : 235,
    );
    const quietPatch = this.scene.add.image(
      safe.centerX + (compact ? 220 : 255),
      safe.centerY + (compact ? 62 : 76),
      POLLINATOR_GARDEN_ASSETS.flowerPatchUnvisited.key,
    ).setDisplaySize(compact ? 205 : 235, compact ? 220 : 250);
    const observePanel = this.scene.add.image(
      safe.centerX,
      safe.centerY - (compact ? 120 : 132),
      POLLINATOR_GARDEN_ASSETS.observePanel.key,
    ).setDisplaySize(compact ? 180 : 205, compact ? 158 : 180);
    const observation = this.makeText(
      this.investigation.config.sceneObservation,
      compact ? 13 : 15,
      '#ffffff',
      'bold',
    ).setPosition(safe.centerX, safe.bottom - (compact ? 111 : 125))
      .setWordWrapWidth(Math.min(780, safe.width - 210));
    const next = this.createPanelButton(
      safe.centerX,
      safe.bottom - (compact ? 50 : 58),
      compact ? 260 : 310,
      compact ? 54 : 62,
      'LOG WHAT YOU NOTICE  ↵',
      () => this.acknowledgeObservation(),
      compact ? 12 : 14,
    );
    this.phaseLayer!.add([openPatch, quietPatch, observePanel, observation, next.container]);
  }

  private renderPredict() {
    const safe = calculateDiscoveryLabSafeBounds(this.scene);
    const compact = isCompactDiscoveryLabLayout(this.scene, safe);
    this.createHeader('PREDICT', this.investigation.config.predictionPrompt);
    const y = safe.centerY + (compact ? 45 : 58);
    const leftX = safe.centerX - (compact ? 225 : 270);
    const rightX = safe.centerX + (compact ? 225 : 270);
    const openPatch = this.createVisitedPatch(leftX, y, compact ? 230 : 270)
      .setInteractive({ useHandCursor: true });
    const quietPatch = this.scene.add.image(
      rightX,
      y,
      POLLINATOR_GARDEN_ASSETS.flowerPatchUnvisited.key,
    ).setDisplaySize(compact ? 230 : 270, compact ? 246 : 288)
      .setInteractive({ useHandCursor: true });
    openPatch.on('pointerup', () => this.choosePrediction('open-to-pollinators'));
    quietPatch.on('pointerup', () => this.choosePrediction('limited-pollinator-visits'));
    const openLabel = this.makeText('1  Watch the open patch', compact ? 15 : 17, '#ffffff', 'bold')
      .setPosition(leftX, y + (compact ? 145 : 170));
    const quietLabel = this.makeText('2  Watch the quiet patch', compact ? 15 : 17, '#ffffff', 'bold')
      .setPosition(rightX, y + (compact ? 145 : 170));
    this.phaseLayer!.add([openPatch, quietPatch, openLabel, quietLabel]);
  }

  private renderInvestigate(snapshot: RelationshipInvestigationSnapshot) {
    const safe = calculateDiscoveryLabSafeBounds(this.scene);
    const compact = isCompactDiscoveryLabLayout(this.scene, safe);
    const observed = new Set(snapshot.observedInteractionIds);
    const detail = this.feedbackMessage
      || `Track visits and changes • ${observed.size}/${this.investigation.config.requiredInteractionIds.length} observations`;
    this.createHeader('INVESTIGATE', detail);

    const patchY = safe.centerY + (compact ? 42 : 55);
    const openX = safe.centerX - (compact ? 240 : 285);
    const quietX = safe.centerX + (compact ? 240 : 285);
    const openPatch = this.createVisitedPatch(openX, patchY, compact ? 210 : 245)
      .setInteractive({ useHandCursor: true });
    const quietPatch = this.scene.add.image(
      quietX,
      patchY,
      POLLINATOR_GARDEN_ASSETS.flowerPatchUnvisited.key,
    ).setDisplaySize(compact ? 210 : 245, compact ? 224 : 262)
      .setInteractive({ useHandCursor: true });
    openPatch.setAlpha(observed.has('inspect-open-patch') ? 0.78 : 1);
    quietPatch.setAlpha(observed.has('inspect-quiet-patch') ? 0.78 : 1);
    openPatch.on('pointerup', () => this.observeInteraction('inspect-open-patch'));
    quietPatch.on('pointerup', () => this.observeInteraction('inspect-quiet-patch'));

    const bee = this.scene.add.image(
      safe.centerX - (compact ? 58 : 70),
      safe.centerY - (compact ? 92 : 108),
      POLLINATOR_GARDEN_ASSETS.bee.key,
    ).setDisplaySize(compact ? 92 : 108, compact ? 98 : 116)
      .setInteractive({ useHandCursor: true });
    const butterfly = this.scene.add.image(
      safe.centerX + (compact ? 66 : 80),
      safe.centerY - (compact ? 94 : 110),
      POLLINATOR_GARDEN_ASSETS.butterfly.key,
    ).setDisplaySize(compact ? 94 : 110, compact ? 95 : 111)
      .setInteractive({ useHandCursor: true });
    bee.on('pointerup', () => this.observeBee());
    butterfly.on('pointerup', () => this.observeButterfly());

    const seedCheckReady = [
      'inspect-open-patch',
      'bee-collects-pollen',
      'bee-carries-pollen',
      'butterfly-visits-flower',
      'inspect-quiet-patch',
    ].every(id => observed.has(id));
    const seedPlate = this.scene.add.image(
      safe.centerX,
      safe.bottom - (compact ? 112 : 126),
      POLLINATOR_GARDEN_ASSETS.flowerPatchSeedPods.key,
    ).setDisplaySize(compact ? 225 : 260, compact ? 174 : 201)
      .setAlpha(seedCheckReady ? 1 : 0.46)
      .setInteractive({ useHandCursor: true });
    seedPlate.on('pointerup', () => this.observeSeedPods(seedCheckReady));

    const labels = [
      this.makeText('1  OPEN PATCH', compact ? 12 : 14, '#ffffff', 'bold')
        .setPosition(openX, patchY + (compact ? 132 : 150)),
      this.makeText('2  BEE', compact ? 12 : 14, '#ffffff', 'bold')
        .setPosition(bee.x, bee.y - (compact ? 63 : 72)),
      this.makeText('3  BUTTERFLY', compact ? 12 : 14, '#ffffff', 'bold')
        .setPosition(butterfly.x, butterfly.y - (compact ? 63 : 72)),
      this.makeText('4  QUIET PATCH', compact ? 12 : 14, '#ffffff', 'bold')
        .setPosition(quietX, patchY + (compact ? 132 : 150)),
      this.makeText('5  CHECK SEED PODS', compact ? 11 : 13, '#ffffff', 'bold')
        .setPosition(seedPlate.x, seedPlate.y + (compact ? 98 : 112)),
    ];
    this.phaseLayer!.add([openPatch, quietPatch, bee, butterfly, seedPlate, ...labels]);

    if (this.investigation.getSnapshot().phase === 'investigate'
      && this.investigation.config.requiredInteractionIds.every(id => observed.has(id))) {
      const collect = this.createPanelButton(
        safe.centerX,
        safe.y + (compact ? 126 : 138),
        compact ? 250 : 290,
        compact ? 48 : 54,
        'COLLECT EVIDENCE  ↵',
        () => this.beginEvidenceCollection(),
        compact ? 12 : 14,
      );
      this.phaseLayer!.add(collect.container);
    }
  }

  private renderEvidence(snapshot: RelationshipInvestigationSnapshot) {
    const safe = calculateDiscoveryLabSafeBounds(this.scene);
    const compact = isCompactDiscoveryLabLayout(this.scene, safe);
    this.createHeader('COLLECT EVIDENCE', this.feedbackMessage || 'Select the three field notes supported by your observations.');
    const panel = this.scene.add.image(
      safe.centerX,
      safe.centerY - (compact ? 105 : 118),
      POLLINATOR_GARDEN_ASSETS.evidencePanel.key,
    ).setCrop(42, 0, 300, 276).setDisplaySize(compact ? 190 : 215, compact ? 175 : 198);

    const collected = new Set(snapshot.collectedEvidenceIds);
    const y = safe.centerY + (compact ? 70 : 82);
    const offset = compact ? 215 : 255;
    const evidence = [
      this.createEvidenceImage(
        safe.centerX - offset,
        y,
        POLLINATOR_GARDEN_ASSETS.visitEvidence.key,
        'animals-visited-flowers',
        '1  ANIMAL VISITS',
        collected,
        compact,
        { x: 55, y: 0, width: 245, height: 264 },
      ),
      this.createEvidenceImage(
        safe.centerX,
        y,
        POLLINATOR_GARDEN_ASSETS.pollenEvidence.key,
        'pollen-moved-between-flowers',
        '2  POLLEN MOVED',
        collected,
        compact,
      ),
      this.createEvidenceImage(
        safe.centerX + offset,
        y,
        POLLINATOR_GARDEN_ASSETS.seedEvidence.key,
        'visited-patch-made-more-seeds',
        '3  MORE SEED PODS',
        collected,
        compact,
        { x: 62, y: 0, width: 256, height: 264 },
      ),
    ];
    this.phaseLayer!.add([panel, ...evidence.flat()]);
  }

  private renderCompare(snapshot: RelationshipInvestigationSnapshot) {
    const safe = calculateDiscoveryLabSafeBounds(this.scene);
    const compact = isCompactDiscoveryLabLayout(this.scene, safe);
    const detail = this.feedbackMessage
      || snapshot.comparison?.evidenceStatement
      || this.investigation.config.comparisonPrompt;
    this.createHeader('COMPARE', detail);

    const evidenceY = safe.centerY - (compact ? 112 : 125);
    const visitEvidence = this.scene.add.image(
      safe.centerX - (compact ? 125 : 145),
      evidenceY,
      POLLINATOR_GARDEN_ASSETS.visitEvidence.key,
    ).setCrop(55, 0, 245, 264).setDisplaySize(compact ? 100 : 116, compact ? 105 : 122);
    const pollenEvidence = this.scene.add.image(
      safe.centerX,
      evidenceY,
      POLLINATOR_GARDEN_ASSETS.pollenEvidence.key,
    ).setDisplaySize(compact ? 100 : 116, compact ? 105 : 122);
    const seedEvidence = this.scene.add.image(
      safe.centerX + (compact ? 125 : 145),
      evidenceY,
      POLLINATOR_GARDEN_ASSETS.seedEvidence.key,
    ).setCrop(62, 0, 256, 264).setDisplaySize(compact ? 100 : 116, compact ? 105 : 122);

    const patchY = safe.centerY + (compact ? 105 : 122);
    const openX = safe.centerX - (compact ? 225 : 265);
    const quietX = safe.centerX + (compact ? 225 : 265);
    const openPatch = this.createVisitedPatch(openX, patchY, compact ? 220 : 255)
      .setInteractive({ useHandCursor: true });
    const quietPatch = this.scene.add.image(
      quietX,
      patchY,
      POLLINATOR_GARDEN_ASSETS.flowerPatchUnvisited.key,
    ).setDisplaySize(compact ? 220 : 255, compact ? 235 : 272)
      .setInteractive({ useHandCursor: true });
    openPatch.on('pointerup', () => this.compareEvidence('open-to-pollinators'));
    quietPatch.on('pointerup', () => this.compareEvidence('limited-pollinator-visits'));
    const prompt = this.makeText(
      'Place your evidence with the patch it supports.  1 = open • 2 = quiet',
      compact ? 13 : 15,
      '#ffffff',
      'bold',
    ).setPosition(safe.centerX, safe.bottom - (compact ? 36 : 42));
    this.phaseLayer!.add([
      visitEvidence,
      pollenEvidence,
      seedEvidence,
      openPatch,
      quietPatch,
      prompt,
    ]);
  }

  private renderApply(snapshot: RelationshipInvestigationSnapshot) {
    const safe = calculateDiscoveryLabSafeBounds(this.scene);
    const compact = isCompactDiscoveryLabLayout(this.scene, safe);
    this.createHeader('APPLY FINDING', snapshot.comparison?.evidenceStatement || this.investigation.config.evidenceStatement);
    const transfer = this.scene.add.image(
      safe.centerX,
      safe.centerY + (compact ? 20 : 28),
      POLLINATOR_GARDEN_ASSETS.pollenTransferVfx.key,
    ).setDisplaySize(compact ? 590 : 680, compact ? 270 : 310);
    const apply = this.scene.add.image(
      safe.centerX,
      safe.bottom - (compact ? 62 : 70),
      POLLINATOR_GARDEN_ASSETS.applyEvidenceButton.key,
    ).setDisplaySize(compact ? 205 : 235, compact ? 141 : 162)
      .setInteractive({ useHandCursor: true });
    apply.on('pointerup', () => this.applyFinding());
    const key = this.makeText('A / ENTER', compact ? 11 : 13, '#ffffff', 'bold')
      .setPosition(apply.x, apply.y + (compact ? 57 : 67));
    this.phaseLayer!.add([transfer, apply, key]);
  }

  private renderComplete() {
    const safe = calculateDiscoveryLabSafeBounds(this.scene);
    const compact = isCompactDiscoveryLabLayout(this.scene, safe);
    this.createHeader('RELATIONSHIP RESTORED', 'Animal visits carry pollen between flowers, and the garden responds!');
    const seedPods = this.scene.add.image(
      safe.centerX - (compact ? 215 : 250),
      safe.centerY + (compact ? 70 : 80),
      POLLINATOR_GARDEN_ASSETS.flowerPatchSeedPods.key,
    ).setDisplaySize(compact ? 300 : 350, compact ? 232 : 270);
    const restoration = this.scene.add.image(
      safe.centerX + (compact ? 175 : 205),
      safe.centerY + (compact ? 34 : 42),
      POLLINATOR_GARDEN_ASSETS.restorationVfx.key,
    ).setDisplaySize(compact ? 500 : 570, compact ? 239 : 273).setAlpha(0);
    const message = this.makeText(
      'The pollinator route is open. Flowers bloom, seed pods grow, and exploration resumes!',
      compact ? 15 : 18,
      '#ffffff',
      'bold',
    ).setPosition(safe.centerX, safe.bottom - (compact ? 48 : 55))
      .setWordWrapWidth(Math.min(820, safe.width - 170));
    this.phaseLayer!.add([seedPods, restoration, message]);

    if (!this.completionQueued) {
      this.completionQueued = true;
      if (this.options.reducedMotion) restoration.setAlpha(0.74);
      else {
        restoration.setScale(restoration.scaleX * 0.82, restoration.scaleY * 0.82);
        this.scene.tweens.add({
          targets: restoration,
          alpha: 0.86,
          scaleX: restoration.scaleX / 0.82,
          scaleY: restoration.scaleY / 0.82,
          duration: 620,
          ease: 'Back.easeOut',
        });
      }
      this.timers.push(this.scene.time.delayedCall(this.options.reducedMotion ? 850 : 1650, () => {
        if (!this.destroyed) this.options.onComplete();
      }));
    }
  }

  private createHeader(phase: string, detail: string) {
    const safe = calculateDiscoveryLabSafeBounds(this.scene);
    const compact = isCompactDiscoveryLabLayout(this.scene, safe);
    const panel = this.scene.add.image(
      safe.centerX,
      safe.y + (compact ? 50 : 56),
      DISCOVERY_LAB_ASSETS.labMissionPanel.key,
    ).setCrop(135, 55, 930, 300)
      .setDisplaySize(Math.min(compact ? 900 : 1010, safe.width - 170), compact ? 86 : 96);
    const title = this.makeText(phase, compact ? 17 : 20, '#9eff9c', 'bold')
      .setPosition(safe.centerX, panel.y - (compact ? 13 : 15));
    const copy = this.makeText(this.hintMessage || detail, compact ? 12 : 14, '#ffffff', 'bold')
      .setPosition(safe.centerX, panel.y + (compact ? 14 : 16))
      .setWordWrapWidth(panel.displayWidth - 150);
    const exit = this.createPanelButton(
      safe.x + (compact ? 56 : 65),
      safe.y + (compact ? 48 : 54),
      compact ? 100 : 116,
      compact ? 54 : 60,
      '← EXIT',
      () => this.options.onExit(),
      compact ? 11 : 13,
    );
    this.phaseLayer!.add([panel, title, copy, exit.container]);

    if (this.investigation.getSnapshot().phase !== 'complete') {
      const hint = this.createPanelButton(
        safe.right - (compact ? 56 : 65),
        safe.y + (compact ? 48 : 54),
        compact ? 100 : 116,
        compact ? 54 : 60,
        'HINT  H',
        () => this.showHint(),
        compact ? 11 : 13,
      );
      this.phaseLayer!.add(hint.container);
      if (this.investigation.getSnapshot().phase !== 'observe') {
        const reset = this.createPanelButton(
          safe.right - (compact ? 164 : 190),
          safe.y + (compact ? 48 : 54),
          compact ? 92 : 108,
          compact ? 50 : 56,
          'RESET  R',
          () => this.resetInvestigation(),
          compact ? 10 : 12,
        );
        this.phaseLayer!.add(reset.container);
      }
    }
  }

  private createEvidenceImage(
    x: number,
    y: number,
    texture: string,
    evidenceId: string,
    label: string,
    collected: ReadonlySet<string>,
    compact: boolean,
    crop?: { x: number; y: number; width: number; height: number },
  ) {
    const image = this.scene.add.image(x, y, texture)
      .setDisplaySize(compact ? 150 : 175, compact ? 160 : 186)
      .setAlpha(collected.has(evidenceId) ? 0.42 : 1)
      .setInteractive({ useHandCursor: true });
    if (crop) image.setCrop(crop.x, crop.y, crop.width, crop.height);
    image.on('pointerup', () => this.collectEvidence(evidenceId));
    const text = this.makeText(
      collected.has(evidenceId) ? `${label}  ✓` : label,
      compact ? 12 : 14,
      '#ffffff',
      'bold',
    ).setPosition(x, y + (compact ? 102 : 118));
    return [image, text] as const;
  }

  private createVisitedPatch(x: number, y: number, width: number) {
    return this.scene.add.image(x, y, POLLINATOR_GARDEN_ASSETS.flowerPatchVisited.key)
      .setCrop(54, 0, 234, 315)
      .setDisplaySize(width, width * 1.08);
  }

  private createPanelButton(
    x: number,
    y: number,
    width: number,
    height: number,
    label: string,
    action: () => void,
    fontSize = 15,
  ): PanelButton {
    const container = this.scene.add.container(x, y);
    const frame = this.scene.add.image(0, 0, DISCOVERY_LAB_ASSETS.labMissionPanel.key)
      .setCrop(135, 55, 930, 300).setDisplaySize(width, height)
      .setInteractive({ useHandCursor: true });
    const text = this.makeText(label, fontSize, '#ffffff', 'bold')
      .setWordWrapWidth(width - 44);
    frame.on('pointerdown', () => {
      if (this.canInteract()) container.setAlpha(0.76);
    });
    frame.on('pointerout', () => container.setAlpha(1));
    frame.on('pointerup', () => {
      container.setAlpha(1);
      if (this.canInteract()) action();
    });
    container.add([frame, text]);
    return { container, frame };
  }

  private makeText(
    value: string,
    size: number,
    color: string,
    fontStyle: 'normal' | 'bold',
  ) {
    return this.scene.add.text(0, 0, value, {
      fontFamily: FONT_UI,
      fontSize: `${size}px`,
      fontStyle,
      color,
      align: 'center',
      lineSpacing: 2,
      stroke: '#173821',
      strokeThickness: 4,
    }).setOrigin(0.5).setResolution(2);
  }

  private acknowledgeObservation() {
    if (this.canInteract() && this.investigation.acknowledgeObservation()) this.render();
  }

  private choosePrediction(conditionId: string) {
    if (this.canInteract() && this.investigation.choosePrediction(conditionId)) this.render();
  }

  private observeInteraction(interactionId: string) {
    if (!this.canInteract()) return;
    const interaction = this.investigation.config.interactions.find(item => item.id === interactionId);
    if (!interaction || !this.investigation.recordInteraction(interactionId)) {
      this.feedbackMessage = 'That observation is already in your field notes. Try another garden clue.';
      this.render();
      return;
    }
    this.feedbackMessage = interaction.observation;
    this.render();
  }

  private observeBee() {
    const observed = new Set(this.investigation.getSnapshot().observedInteractionIds);
    if (!observed.has('inspect-open-patch')) {
      this.feedbackMessage = 'Inspect the open flower patch first. Then watch where the bee lands.';
      this.render();
      return;
    }
    const next = !observed.has('bee-collects-pollen')
      ? 'bee-collects-pollen'
      : !observed.has('bee-carries-pollen')
        ? 'bee-carries-pollen'
        : undefined;
    if (!next) {
      this.feedbackMessage = 'Both bee observations are recorded. Look for the butterfly or quiet patch next.';
      this.render();
      return;
    }
    this.observeInteraction(next);
    this.playPollinatorCue(POLLINATOR_GARDEN_ASSETS.bee.key);
  }

  private observeButterfly() {
    const observed = new Set(this.investigation.getSnapshot().observedInteractionIds);
    if (!observed.has('inspect-open-patch')) {
      this.feedbackMessage = 'Inspect the open flower patch first, then follow the butterfly.';
      this.render();
      return;
    }
    if (observed.has('butterfly-visits-flower')) {
      this.feedbackMessage = 'The butterfly visit is already recorded. Check the other patch or seed pods.';
      this.render();
      return;
    }
    this.observeInteraction('butterfly-visits-flower');
    this.playPollinatorCue(POLLINATOR_GARDEN_ASSETS.butterfly.key);
  }

  private observeSeedPods(ready: boolean) {
    if (!ready) {
      this.feedbackMessage = 'Keep watching animal visits in both patches before checking what grows later.';
      this.render();
      return;
    }
    this.observeInteraction('compare-seed-pods');
  }

  private playPollinatorCue(texture: string) {
    if (this.destroyed || !this.phaseLayer) return;
    const safe = calculateDiscoveryLabSafeBounds(this.scene);
    const compact = isCompactDiscoveryLabLayout(this.scene, safe);
    const cue = this.scene.add.image(
      safe.centerX - (compact ? 170 : 205),
      safe.centerY - 10,
      texture,
    ).setDisplaySize(compact ? 92 : 108, compact ? 98 : 116).setAlpha(0.95);
    const pollen = this.scene.add.image(
      safe.centerX,
      safe.centerY + 8,
      POLLINATOR_GARDEN_ASSETS.pollenTransferVfx.key,
    ).setDisplaySize(compact ? 410 : 470, compact ? 187 : 215).setAlpha(0.6);
    this.phaseLayer.add([pollen, cue]);
    if (this.options.reducedMotion) return;
    this.scene.tweens.add({
      targets: cue,
      x: safe.centerX + (compact ? 165 : 200),
      duration: 720,
      ease: 'Sine.easeInOut',
    });
    this.scene.tweens.add({
      targets: pollen,
      alpha: { from: 0.22, to: 0.74 },
      duration: 520,
      yoyo: true,
      ease: 'Sine.easeInOut',
    });
  }

  private beginEvidenceCollection() {
    if (this.canInteract() && this.investigation.beginEvidenceCollection()) this.render();
  }

  private collectEvidence(evidenceId: string) {
    if (!this.canInteract()) return;
    const evidence = this.investigation.config.evidenceTargets.find(item => item.id === evidenceId);
    if (!evidence || !this.investigation.collectEvidence(evidenceId)) {
      this.feedbackMessage = 'That field note is already collected. Choose another observed clue.';
      this.render();
      return;
    }
    this.feedbackMessage = evidence.label;
    this.render();
  }

  private compareEvidence(conditionId: string) {
    if (!this.canInteract()) return;
    const result = this.investigation.compareEvidence(conditionId);
    if (!result) return;
    this.feedbackMessage = result.evidenceStatement;
    this.render();
  }

  private applyFinding() {
    if (this.canInteract() && this.investigation.applyFinding()) this.render();
  }

  private resetInvestigation() {
    if (!this.canInteract()) return;
    this.investigation.reset();
    this.hintPhase = undefined;
    this.hintStep = 0;
    this.hintMessage = '';
    this.feedbackMessage = '';
    this.render();
  }

  private showHint() {
    if (!this.canInteract()) return;
    const phase = this.investigation.getSnapshot().phase;
    if (phase === 'complete') return;
    const key = phase === 'collect-evidence' ? 'collectEvidence' : phase;
    const phaseHints = this.investigation.config.hints[key];
    this.hintMessage = phaseHints[Math.min(this.hintStep, phaseHints.length - 1)] ?? '';
    this.hintStep = Math.min(this.hintStep + 1, phaseHints.length - 1);
    this.render();
  }

  private canInteract() {
    return !this.destroyed && !this.suspended;
  }

  private readonly handleKeyDown = (event: KeyboardEvent) => {
    if (!this.canInteract()) return;
    const snapshot = this.investigation.getSnapshot();
    const key = event.key.toLowerCase();
    if (event.key === 'Escape') {
      event.preventDefault();
      this.options.onExit();
      return;
    }
    if (key === 'h') {
      event.preventDefault();
      this.showHint();
      return;
    }
    if (key === 'r' && snapshot.phase !== 'observe' && snapshot.phase !== 'complete') {
      event.preventDefault();
      this.resetInvestigation();
      return;
    }
    if (snapshot.phase === 'observe' && (event.key === 'Enter' || event.key === ' ' || key === 'o')) {
      event.preventDefault();
      this.acknowledgeObservation();
      return;
    }
    if (snapshot.phase === 'predict' && (event.key === '1' || event.key === '2')) {
      event.preventDefault();
      this.choosePrediction(event.key === '1' ? 'open-to-pollinators' : 'limited-pollinator-visits');
      return;
    }
    if (snapshot.phase === 'investigate') {
      event.preventDefault();
      if (event.key === '1') this.observeInteraction('inspect-open-patch');
      else if (event.key === '2') this.observeBee();
      else if (event.key === '3') this.observeButterfly();
      else if (event.key === '4') this.observeInteraction('inspect-quiet-patch');
      else if (event.key === '5') {
        const observed = new Set(snapshot.observedInteractionIds);
        this.observeSeedPods([
          'inspect-open-patch',
          'bee-collects-pollen',
          'bee-carries-pollen',
          'butterfly-visits-flower',
          'inspect-quiet-patch',
        ].every(id => observed.has(id)));
      } else if (event.key === 'Enter' || event.key === ' ') this.beginEvidenceCollection();
      return;
    }
    if (snapshot.phase === 'collect-evidence' && event.key >= '1' && event.key <= '3') {
      event.preventDefault();
      const evidence = this.investigation.config.requiredEvidenceIds[Number(event.key) - 1];
      if (evidence) this.collectEvidence(evidence);
      return;
    }
    if (snapshot.phase === 'compare' && (event.key === '1' || event.key === '2')) {
      event.preventDefault();
      this.compareEvidence(event.key === '1' ? 'open-to-pollinators' : 'limited-pollinator-visits');
      return;
    }
    if (snapshot.phase === 'apply' && (key === 'a' || event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this.applyFinding();
    }
  };
}
